/**
 * Stock-bin identity — the QR codec for finished-garment shelf BIN labels (garment-stock bin-labels
 * plan v1 § 3.1). Same charter exception as `garment-identity.ts`: a pure, IO-free serialization of a
 * contract-defined identity, shared by the dashboard (which renders the QR) and the rest-api (which
 * dispatches a scanned code) — so the grammar lives once, here.
 *
 * Payload: `s1|<binId>` where `binId` is 12 lowercase hex chars — the first 12 of
 * sha256(stockKey), derived SERVER-side only (`garment-stock-store.ts`). The stockKey itself is
 * deliberately NOT in the QR: at 105–235 chars it forces QR version 11–13, ~0.3mm/module at the
 * 17–20mm label sizes — below the 0.486mm/module pitch the hardware spike validated
 * (docs/label-printer-formats-plan-v1.md § QR). The short id keeps the symbol at version 2.
 *
 * Collision with garment codes is impossible by construction: `decodeGarmentCode` requires exactly
 * 5 pipe-fields with a literal `v1` tag; this codec requires exactly 2 with a literal `s1` tag.
 * Both decoders return `null` for the other's payloads (cross-tested in stock-bin-identity.test.ts).
 */
import { z } from 'zod';
/** Namespace/version tag. A scanned code not starting with this decodes to `null`. */
export const STOCK_BIN_CODE_VERSION = 's1';
const DELIM = '|';
const MAX_CODE_LENGTH = 256;
/** 12 lowercase hex chars — the server-derived sha256(stockKey) slice. */
const BIN_ID_RE = /^[0-9a-f]{12}$/;
export const StockBinCodeSchema = z.object({
    version: z.literal(STOCK_BIN_CODE_VERSION),
    binId: z.string().regex(BIN_ID_RE),
});
/**
 * Encode a bin id into the QR payload string. Throws on a malformed id — the id always comes from a
 * server view (`GarmentStockOnHand.binId`), so a bad one is a caller bug, never operator input.
 */
export function encodeStockBinCode(binId) {
    if (!BIN_ID_RE.test(binId)) {
        throw new Error('encodeStockBinCode: binId must be 12 lowercase hex chars');
    }
    return [STOCK_BIN_CODE_VERSION, binId].join(DELIM);
}
/**
 * Decode a scanned string into a stock-bin code, or `null` when it is not one. NEVER throws — the
 * scan station feeds every QR it sees through every decoder, and `null` just means "not this kind".
 */
export function decodeStockBinCode(raw) {
    if (typeof raw !== 'string')
        return null;
    const trimmed = raw.trim();
    if (trimmed.length === 0 || trimmed.length > MAX_CODE_LENGTH)
        return null;
    const parts = trimmed.split(DELIM);
    if (parts.length !== 2)
        return null;
    const [version, binId] = parts;
    if (version !== STOCK_BIN_CODE_VERSION)
        return null; // foreign or newer-scheme code
    if (binId === undefined || !BIN_ID_RE.test(binId))
        return null;
    const result = StockBinCodeSchema.safeParse({ version, binId });
    return result.success ? result.data : null;
}
//# sourceMappingURL=stock-bin-identity.js.map